/**
 * ContactCardBlock — digital business-card block for Bio Hub / Action Pages.
 *
 * Renders a card with:
 *   • Name + Title (always shown; title hidden if blank)
 *   • Phone / WhatsApp / Email icons — each rendered ONLY if the matching
 *     field is filled. Tap opens tel: / wa.me / mailto:
 *   • Small QR code that encodes the contact as a vCard 3.0. Scanning
 *     with the native camera prompts "Add to Contacts".
 *   • "Save contact" button that downloads a `.vcf` file. iOS + Android
 *     recognise this and offer to save into the address book.
 *
 * The block is renderer-only (no editor logic here); config comes from
 * `block.config` prepared by `BlockEditor.jsx`.
 */
import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Phone as PhoneIcon, Mail as MailIcon, MessageCircle, UserPlus } from "lucide-react";

/** Build a minimal RFC-6350-compatible vCard 3.0 string. */
function buildVCard({ name, title, phone, whatsapp, email }) {
  const fold = (s) => (s || "").replace(/\r?\n/g, " ").trim();
  const N = fold(name) || "Contact";
  const parts = ["BEGIN:VCARD", "VERSION:3.0", `FN:${N}`];
  // vCard N field is Family;Given;Additional;Prefix;Suffix — we don't split
  // the display name so put everything in the Family slot.
  parts.push(`N:${N};;;;`);
  if (title) parts.push(`TITLE:${fold(title)}`);
  if (phone) parts.push(`TEL;TYPE=CELL:${fold(phone)}`);
  // WhatsApp is a mobile number too — many phones just merge it into the
  // Phone entry if it's identical, otherwise adds a second CELL number.
  if (whatsapp && whatsapp !== phone) parts.push(`TEL;TYPE=CELL,VOICE:${fold(whatsapp)}`);
  if (email) parts.push(`EMAIL;TYPE=INTERNET:${fold(email)}`);
  parts.push("END:VCARD");
  return parts.join("\n");
}

function normaliseWhatsApp(number) {
  // Strip spaces / dashes / plus for the wa.me URL.
  return (number || "").replace(/[^0-9]/g, "");
}

/** Returns 1-2 uppercase initials from a display name for the avatar fallback. */
function getInitials(name) {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function safeFileName(name) {
  return (name || "contact").trim().replace(/[^\w\-]+/g, "_").slice(0, 40) || "contact";
}

// v146.22 — Long-press → save vCard. Users who press-and-hold ANY icon
// on the mini card (Call / WhatsApp / Email) for 500ms get the full
// contact downloaded as a .vcf file instead of triggering the icon's
// native action. Works with mouse + touch + pen. `disabled` skips the
// binding entirely inside the editor where the tap should be inert.
function useLongPressVCard({ vcard, filename, disabled = false }) {
  const timerRef = useRef(null);
  const triggeredRef = useRef(false);

  const downloadVCard = () => {
    triggeredRef.current = true;
    try {
      const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.vcf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contact card saved to Downloads");
    } catch {
      toast.error("Could not save contact");
    }
  };

  const start = (e) => {
    if (disabled) return;
    triggeredRef.current = false;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Prevent the icon's default nav (tel:/mailto:/wa.me) once the hold
      // wins. We do this by flagging triggeredRef so the click handler
      // below intercepts and bails out.
      downloadVCard();
    }, 500);
  };
  const cancel = () => { clearTimeout(timerRef.current); };
  const onClick = (e) => {
    if (triggeredRef.current) {
      // Long-press already fired — swallow the click so we don't ALSO
      // open Phone / WhatsApp / Email after saving.
      e.preventDefault();
      triggeredRef.current = false;
    }
  };

  return {
    onPointerDown: start,
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onClick,
  };
}

// v145.50 — Contact card colour presets. `theme=custom` (the implicit
// default) keeps the ah-theme CSS-variable inheritance untouched.
const THEMES = {
  mono:  { bg: "#ffffff", fg: "#111111", accent: "#111111", muted: "#111111aa" },
  teal:  { bg: "#0f766e", fg: "#f6f1e6", accent: "#f6f1e6", muted: "#f6f1e6cc" },
  navy:  { bg: "#0f172a", fg: "#f6f1e6", accent: "#facc15", muted: "#e5e7eb" },
  coral: { bg: "#ff5a3c", fg: "#ffffff", accent: "#111111", muted: "#ffffffcc" },
};

export default function ContactCardBlock({ block, editable = false }) {
  const c = block?.config || {};
  const {
    name = "",
    title = "",
    phone = "",
    whatsapp = "",
    email = "",
    photo_url = "",
    theme = "",
    show_qr = true,
    qr_size = 96,
    mini = false,
  } = c;

  const displayName = name.trim() || "Contact";
  const hasPhone = !!phone.trim();
  const hasWhatsApp = !!whatsapp.trim();
  const hasEmail = !!email.trim();

  // v146.21 — Mini variant. When `c.mini === true` the card collapses
  // to a single row of clickable icons (Call · WhatsApp · Email) with
  // nothing else — no avatar, name, title, QR, or Save-contact button.
  // Matches the socials block visual language so creators can drop a
  // slim "reach me" row on any Bio or Action page.
  //
  // v146.22 — Long-press (500ms) any icon → downloads the full vCard
  // so visitors who want to "save the whole contact" don't lose it.
  const miniVcard = mini ? buildVCard({ name: displayName, title, phone, whatsapp, email }) : "";
  const miniFile = mini ? safeFileName(displayName) : "";
  const longPressBind = useLongPressVCard({
    vcard: miniVcard,
    filename: miniFile,
    disabled: !mini || editable,
  });
  if (mini) {
    const iconCls = "w-11 h-11 sm:w-12 sm:h-12 border-2 border-ink inline-flex items-center justify-center hover:-translate-y-0.5 transition-transform select-none touch-manipulation";
    return (
      <div
        className="ah-card contact-card contact-card-mini p-3"
        data-testid={`ah-block-contact-card-${block?.block_id || "preview"}`}
        style={{
          background: "var(--ah-block-bg)",
          color: "var(--ah-block-fg)",
          border: "var(--ah-block-border, 2px solid #111)",
          borderRadius: "var(--ah-block-radius, 0)",
          boxShadow: "var(--ah-block-shadow, none)",
        }}
      >
        <div className="flex items-center justify-center gap-3">
          {hasPhone && (
            <a
              href={editable ? "#" : `tel:${phone.replace(/\s+/g, "")}`}
              onClick={(e) => { longPressBind.onClick(e); if (editable) e.preventDefault(); }}
              onPointerDown={longPressBind.onPointerDown}
              onPointerUp={longPressBind.onPointerUp}
              onPointerLeave={longPressBind.onPointerLeave}
              onPointerCancel={longPressBind.onPointerCancel}
              onContextMenu={(e) => e.preventDefault()}
              className={iconCls}
              data-testid="contact-card-mini-phone"
              aria-label="Call — long-press to save contact"
              title="Tap to call · Long-press to save contact"
              style={{ background: "var(--ah-block-bg)", color: "var(--ah-block-fg)" }}
            >
              <PhoneIcon className="w-5 h-5" strokeWidth={2.5} />
            </a>
          )}
          {hasWhatsApp && (
            <a
              href={editable ? "#" : `https://wa.me/${normaliseWhatsApp(whatsapp)}`}
              onClick={(e) => { longPressBind.onClick(e); if (editable) e.preventDefault(); }}
              onPointerDown={longPressBind.onPointerDown}
              onPointerUp={longPressBind.onPointerUp}
              onPointerLeave={longPressBind.onPointerLeave}
              onPointerCancel={longPressBind.onPointerCancel}
              onContextMenu={(e) => e.preventDefault()}
              target={editable ? undefined : "_blank"}
              rel="noopener noreferrer"
              className={iconCls}
              data-testid="contact-card-mini-whatsapp"
              aria-label="WhatsApp — long-press to save contact"
              title="Tap for WhatsApp · Long-press to save contact"
              style={{ background: "#25d366", color: "#fff", borderColor: "#0f7a3d" }}
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
            </a>
          )}
          {hasEmail && (
            <a
              href={editable ? "#" : `mailto:${email}`}
              onClick={(e) => { longPressBind.onClick(e); if (editable) e.preventDefault(); }}
              onPointerDown={longPressBind.onPointerDown}
              onPointerUp={longPressBind.onPointerUp}
              onPointerLeave={longPressBind.onPointerLeave}
              onPointerCancel={longPressBind.onPointerCancel}
              onContextMenu={(e) => e.preventDefault()}
              className={iconCls}
              data-testid="contact-card-mini-email"
              aria-label="Email — long-press to save contact"
              title="Tap to email · Long-press to save contact"
              style={{ background: "var(--ah-block-bg)", color: "var(--ah-block-fg)" }}
            >
              <MailIcon className="w-5 h-5" strokeWidth={2.5} />
            </a>
          )}
          {!hasPhone && !hasWhatsApp && !hasEmail && (
            <span className="text-xs text-ink/60 italic">
              Add a phone, WhatsApp or email to show icons here.
            </span>
          )}
        </div>
        {(hasPhone || hasWhatsApp || hasEmail) && (
          <p className="text-[10px] text-ink/50 text-center mt-2 font-medium">
            Tap to reach · Hold to save contact
          </p>
        )}
      </div>
    );
  }

  const hasPhoto = !!photo_url.trim();
  const vcard = buildVCard({ name: displayName, title, phone, whatsapp, email });
  // When a preset is picked, we override the CSS-variable-driven ah-theme
  // colours for THIS card only, so it stands out on the hub without
  // needing any global theme change.
  const preset = THEMES[theme] || null;

  const handleSave = () => {
    if (editable) return; // no-op inside editor preview
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFileName(displayName)}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const iconBtnCls = "border-2 px-3 py-2 inline-flex items-center gap-2 text-sm font-bold hover:translate-y-[1px] transition-transform";
  // For non-mono presets, style the default icon buttons with a contrasting
  // outlined look. WhatsApp always keeps its brand green.
  const btnStyle = preset ? { borderColor: preset.accent, color: preset.fg } : undefined;

  return (
    <div
      className="ah-card contact-card p-4"
      data-testid={`ah-block-contact-card-${block?.block_id || "preview"}`}
      style={{
        background: preset ? preset.bg : "var(--ah-block-bg)",
        color: preset ? preset.fg : "var(--ah-block-fg)",
        border: preset ? `2px solid ${preset.accent}` : "var(--ah-block-border, 2px solid #111)",
        borderRadius: "var(--ah-block-radius, 0)",
        boxShadow: "var(--ah-block-shadow, none)",
      }}
    >
      {/* Top row — avatar / initials (left) · name+title+icons (middle) · QR (right)
          v145.51: compact single-row header keeps the whole card ~2× shorter. */}
      <div className="flex gap-3 items-start">
        {/* Avatar or initials */}
        {hasPhoto ? (
          <img
            src={photo_url}
            alt={displayName}
            data-testid="contact-card-photo"
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover border-2 shrink-0"
            style={preset ? { borderColor: preset.accent } : { borderColor: "currentColor" }}
            onError={(e) => {
              // Fallback to initials on load failure.
              e.currentTarget.style.display = "none";
              const sib = e.currentTarget.nextElementSibling;
              if (sib && sib.dataset && sib.dataset.initialsFallback === "true") {
                sib.style.display = "flex";
              }
            }}
          />
        ) : null}
        {/* Initials fallback (always in DOM if !hasPhoto; hidden but ready if photo fails) */}
        <div
          data-testid="contact-card-initials"
          data-initials-fallback="true"
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border-2 shrink-0 items-center justify-center font-black text-lg sm:text-xl uppercase select-none"
          style={{
            display: hasPhoto ? "none" : "flex",
            borderColor: preset ? preset.accent : "currentColor",
            background: preset ? preset.accent : "currentColor",
            color: preset ? preset.bg : "var(--ah-block-bg)",
          }}
        >
          {getInitials(displayName)}
        </div>

        {/* Middle: name + title + icon row */}
        <div className="flex-1 min-w-0">
          <h3
            className="font-black text-lg sm:text-xl leading-tight break-words"
            style={{
              fontFamily: "var(--ah-title-family)",
              fontWeight: "var(--ah-title-weight, 900)",
              letterSpacing: "var(--ah-title-tracking, normal)",
            }}
          >
            {displayName}
          </h3>
          {title.trim() && (
            <p className="text-xs sm:text-sm opacity-80 mt-0.5 break-words">{title}</p>
          )}
          {(hasPhone || hasWhatsApp || hasEmail) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {hasPhone && (
                <a
                  href={editable ? "#" : `tel:${phone.replace(/\s+/g, "")}`}
                  onClick={(e) => editable && e.preventDefault()}
                  className={iconBtnCls}
                  style={btnStyle}
                  data-testid="contact-card-phone"
                  aria-label={`Call ${displayName}`}
                >
                  <PhoneIcon className="w-3.5 h-3.5" strokeWidth={2.5} /> Call
                </a>
              )}
              {hasWhatsApp && (
                <a
                  href={editable ? "#" : `https://wa.me/${normaliseWhatsApp(whatsapp)}`}
                  onClick={(e) => editable && e.preventDefault()}
                  target={editable ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  className={iconBtnCls}
                  data-testid="contact-card-whatsapp"
                  aria-label={`WhatsApp ${displayName}`}
                  style={{ background: "#25d366", color: "#fff", borderColor: "#0f7a3d" }}
                >
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.5} /> WhatsApp
                </a>
              )}
              {hasEmail && (
                <a
                  href={editable ? "#" : `mailto:${email}`}
                  onClick={(e) => editable && e.preventDefault()}
                  className={iconBtnCls}
                  style={btnStyle}
                  data-testid="contact-card-email"
                  aria-label={`Email ${displayName}`}
                >
                  <MailIcon className="w-3.5 h-3.5" strokeWidth={2.5} /> Email
                </a>
              )}
            </div>
          )}
        </div>

        {/* Right: small QR in top-right corner */}
        {show_qr && (
          <div className="flex flex-col items-center shrink-0" data-testid="contact-card-qr">
            <div
              className="border-2 p-1 bg-white"
              style={preset ? { borderColor: preset.accent } : { borderColor: "currentColor" }}
              title="Scan to save contact"
            >
              <QRCodeCanvas
                value={vcard}
                size={Math.max(56, Math.min(96, Number(qr_size) || 72))}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#111111"
              />
            </div>
            <span className="text-[9px] font-bold opacity-70 mt-1 uppercase tracking-wider">Scan</span>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={editable}
        data-testid="contact-card-save-btn"
        className="w-full mt-3 border-2 px-4 py-2 font-black text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 disabled:opacity-60"
        style={preset
          ? { borderColor: preset.accent, background: preset.fg, color: preset.bg }
          : { borderColor: "var(--ah-block-fg)", background: "var(--ah-block-fg)", color: "var(--ah-block-bg)" }}
      >
        <UserPlus className="w-4 h-4" strokeWidth={2.5} />
        Save contact
      </button>
    </div>
  );
}
